import { Mutation, Query } from '@apollo/client/react/components';
import EllipsisIcon from '@clearkit/icons/glyphs/Ellipsis';
import Lock from '@clearkit/icons/glyphs/Lock';
import Divider from '@material-ui/core/Divider';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Menu from '@material-ui/core/Menu';
import { CKButton } from 'clearkit';
import startCase from 'lodash/startCase';
import { Link } from 'react-router-dom';
import { Subscribe } from 'unstated';

import ConfirmDeleteModal from '~/components/ConfirmDeleteModal';
import MenuItem from '~/components/RedesignedMenuItem';
import Tooltip from '~/components/RedesignedTooltip';
import ToastsContainer from '~/containers/Toasts';
import UserContainer from '~/containers/User';
import {
  useHasLegacyPlan,
  useHasOperationsAccess,
} from '~/hooks/account/usePlatformAccess';
import paths from '~/lib/paths';
import {
  segmentActionLocations,
  trackSegmentCloned,
  trackSegmentDeleted,
} from '~/lib/segmentAnalytics';

import CloneIcon from './Clone.svg';
import cloneSegmentMutation from './cloneSegmentMutation.graphql';
import CsvExportDialog from './CsvExportDialog';
import DeleteIcon from './Delete.svg';
import deleteSegmentMutation from './deleteSegmentMutation.graphql';
import ExportIcon from './Export.svg';
import ImportIcon from './Import.svg';
import RefreshIcon from './Refresh.svg';
import segmentActionsDeleteQuery from './segmentActionsDeleteQuery.graphql';

function ExportCsvMenuItem({ segment, onClick }) {
  const hasOperations = useHasOperationsAccess();
  const hasLegacyPlan = useHasLegacyPlan();

  const exportAccess = hasOperations || hasLegacyPlan;

  if (!exportAccess) {
    return (
      <Tooltip
        title="Contact customer success to export CSVs from audiences."
        placement="left">
        <span>
          <MenuItem large disabled>
            <ListItemIcon>
              <Lock />
            </ListItemIcon>
            Export {segment && 'Audience'} CSV...
          </MenuItem>
        </span>
      </Tooltip>
    );
  }

  return (
    <Subscribe to={[UserContainer]}>
      {(userContainer) => {
        const objectAction = 'export';
        const objectType = 'csv';
        const hasPermission = userContainer.hasPermissionTo({
          objectAction,
          objectType,
        });

        if (hasPermission) {
          return (
            <MenuItem large onClick={onClick}>
              <ListItemIcon>
                <ExportIcon />
              </ListItemIcon>
              Export {segment && 'Audience'} CSV...
            </MenuItem>
          );
        }

        return (
          <Tooltip
            title={
              "You don't have permission to access this feature. Contact your team Admin for access."
            }
            placement="left"
            style={{ cursor: 'pointer' }}>
            <div>
              <MenuItem large disabled>
                <ListItemIcon>
                  <Lock />
                </ListItemIcon>
                Export {segment && 'Audience'} CSV...
              </MenuItem>
            </div>
          </Tooltip>
        );
      }}
    </Subscribe>
  );
}

class SegmentActionsMenu extends React.Component {
  state = {
    open: false,
    confirmDeleteOpen: false,
    exportCsvOpen: false,
    anchor: null,
  };

  showMenu = (event) => {
    event.preventDefault();

    this.setState({
      open: true,
      anchor: event.currentTarget,
    });
  };

  closeMenu = () => {
    this.setState({
      open: false,
    });
  };

  handleRefresh = () => {
    this.props.onRefresh();

    this.setState({ open: false });
  };

  handleDeleteComplete = () => {
    const { onDelete, segment, location } = this.props;

    trackSegmentDeleted(segment, location);
    ToastsContainer.addSuccess('Audience deleted');

    return this.setState({ open: false }, () => onDelete && onDelete());
  };

  handleCloneComplete = ({ response: segment }) => {
    const { onClone, location, segment: clonedSegment } = this.props;

    trackSegmentCloned(segment, clonedSegment, location);
    ToastsContainer.addSuccess('Audience cloned');

    return this.setState({ open: false }, () => onClone(segment.node));
  };

  exportCsvDialog = () => {
    this.setState({ exportCsvOpen: true, open: false });
  };

  closeExportCsvDialog = () => {
    this.setState({ exportCsvOpen: false });
  };

  renderImportCsvMenuItem = () => {
    const { type } = this.props;

    return (
      <Subscribe to={[UserContainer]}>
        {(userContainer) => {
          const objectAction = 'export';
          const objectType = 'csv';
          const hasPermission = userContainer.hasPermissionTo({
            objectAction,
            objectType,
          });

          if (hasPermission) {
            return (
              <MenuItem
                key="import"
                large
                onClick={this.importCsv}
                component={Link}
                to={paths.importCsv(type)}>
                <ListItemIcon>
                  <ImportIcon />
                </ListItemIcon>
                Import {type === 'Person' ? 'people' : 'companies'} from CSV...
              </MenuItem>
            );
          }

          return (
            <Tooltip
              title={
                "You don't have permission to access this feature. Contact your team Admin for access."
              }
              key="import"
              placement="left"
              style={{ cursor: 'pointer' }}>
              <div>
                <MenuItem key="import" large disabled>
                  <ListItemIcon>
                    <Lock />
                  </ListItemIcon>
                  Import {type === 'Person' ? 'people' : 'companies'} from
                  CSV...
                </MenuItem>
              </div>
            </Tooltip>
          );
        }}
      </Subscribe>
    );
  };

  renderCloneSegmentMenuItem = () => {
    const { segment, type } = this.props;

    return (
      <Mutation
        key="clone"
        mutation={cloneSegmentMutation}
        refetchQueries={['SegmentsMenu']}
        onCompleted={this.handleCloneComplete}
        variables={{ input: { segmentId: segment.id, type } }}>
        {(cloneSegment, { _loading }) => (
          <MenuItem variant="primary" large onClick={cloneSegment}>
            <ListItemIcon>
              <CloneIcon />
            </ListItemIcon>
            Clone Audience
          </MenuItem>
        )}
      </Mutation>
    );
  };

  renderRefreshSegmentMenuItem = () => {
    // Don't render this in the Segment list menus
    if (!this.props.onRefresh) {
      return null;
    }

    return (
      <MenuItem
        variant="primary"
        large
        onClick={this.handleRefresh}
        disabled={this.props.segment?.searchDependencyErrors}>
        <ListItemIcon>
          <RefreshIcon />
        </ListItemIcon>
        Refresh Audience
      </MenuItem>
    );
  };

  renderDeleteSegmentMenuItem = () => (
    <MenuItem
      key="delete"
      onClick={() => this.setState({ open: false, confirmDeleteOpen: true })}
      variant="destructive">
      <ListItemIcon>
        <DeleteIcon />
      </ListItemIcon>
      Delete Audience
    </MenuItem>
  );

  render() {
    const { exportCsv, importCsv, segment, searchQuery, type } = this.props;
    const { anchor, confirmDeleteOpen, exportCsvOpen, open } = this.state;

    const buttonComponent = this.props.buttonComponent || (
      <CKButton className="flex w-12">
        <EllipsisIcon height={32} />
      </CKButton>
    );

    const button = React.cloneElement(buttonComponent, {
      onClick: this.showMenu,
    });

    if (!open && !confirmDeleteOpen && !exportCsvOpen) {
      return button;
    }

    return (
      <>
        {button}

        <Menu
          anchorEl={anchor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          getContentAnchorEl={null}
          open={open}
          onClose={this.closeMenu}>
          {importCsv && this.renderImportCsvMenuItem()}
          {exportCsv && (
            <ExportCsvMenuItem
              key="export"
              segment={segment}
              onClick={this.exportCsvDialog}
            />
          )}
          {segment && [
            this.renderCloneSegmentMenuItem(),
            <Divider key="divider-1" />,
            this.renderRefreshSegmentMenuItem(),
            <Divider key="divider-2" />,
            this.renderDeleteSegmentMenuItem(),
          ]}
        </Menu>

        {confirmDeleteOpen && (
          <Query
            query={segmentActionsDeleteQuery}
            variables={{ segmentId: segment.id, segmentType: segment.type }}>
            {({ loading, error, data }) => {
              if (error || loading) return null;

              const {
                segment: { destinations },
              } = data;

              const destinationsForDelete = destinations.map(
                (destination) =>
                  `${startCase(destination.type)} - ${destination.description}`,
              );

              return (
                <Mutation
                  mutation={deleteSegmentMutation}
                  onCompleted={this.handleDeleteComplete}
                  variables={{
                    input: {
                      id: segment.id,
                      type,
                    },
                  }}
                  refetchQueries={['SegmentsMenu', 'SegmentActivity']}>
                  {(deleteSegment, { _loading }) => (
                    <ConfirmDeleteModal
                      associatedItemNames={destinationsForDelete}
                      open
                      subTitle="You won’t be able to recover this audience once it’s been deleted."
                      deleteItem={deleteSegment}
                      closeModal={() =>
                        this.setState({ confirmDeleteOpen: false })
                      }
                      canDelete
                      type="segment"
                    />
                  )}
                </Mutation>
              );
            }}
          </Query>
        )}

        <CsvExportDialog
          onClose={this.closeExportCsvDialog}
          open={exportCsvOpen}
          searchQuery={searchQuery}
          segment={segment}
          type={type}
        />
      </>
    );
  }
}

SegmentActionsMenu.propTypes = {
  onRefresh: PropTypes.func,
  exportCsv: PropTypes.bool,
  importCSV: PropTypes.bool,
  onClone: PropTypes.func,
  onDelete: PropTypes.func,
  buttonComponent: PropTypes.node,
  segment: PropTypes.shape({
    __typename: PropTypes.string,
    node: PropTypes.string,
    id: PropTypes.string,
  }),
  searchQuery: PropTypes.string,
  type: PropTypes.oneOf(['Company', 'Person']),
  location: PropTypes.oneOf(Object.values(segmentActionLocations)).isRequired,
};

SegmentActionsMenu.defaultProps = {
  exportCsv: false,
  importCsv: false,
  open: false,
};

export default SegmentActionsMenu;
